import React from 'react';
import styled, { keyframes } from 'styled-components';

interface ErrorNoticeCardProps {
  title: string;
  content: string;
  timestamp?: string;
}

const ErrorNoticeCard: React.FC<ErrorNoticeCardProps> = ({ title, content, timestamp = 'just now' }) => {
  return (
    <StyledWrapper>
      <div className="card">
        <div className="img error-icon" />
        <div className="textBox">
          <div className="textContent">
            <p className="h1">{title}</p>
            <span className="span">{timestamp}</span>
          </div>
          <p className="p">{content}</p>
        </div>
      </div>
    </StyledWrapper>
  );
};

const shakeAndFadeIn = keyframes`
  0% {
    transform: translateX(-100%) rotate(-5deg);
    opacity: 0;
  }
  15% {
    transform: translateX(0) rotate(2deg);
    opacity: 0.8;
  }
  30% {
    transform: translateX(-10px) rotate(-1deg);
  }
  45% {
    transform: translateX(10px) rotate(1deg);
  }
  60% {
    transform: translateX(-5px) rotate(-0.5deg);
  }
  75% {
    transform: translateX(5px) rotate(0.5deg);
  }
  100% {
    transform: translateX(0) rotate(0deg);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0%, 100% {
    box-shadow: 0 4px 15px rgba(244, 67, 54, 0.3);
  }
  50% {
    box-shadow: 0 4px 20px rgba(244, 67, 54, 0.5);
  }
`;

const StyledWrapper = styled.div`
  .card {
    width: 100%;
    height: 80px;
    background: linear-gradient(135deg, #F44336, #D32F2F);
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: left;
    backdrop-filter: blur(10px);
    transition: 0.3s ease-in-out;
    animation: ${shakeAndFadeIn} 0.8s ease-out, ${pulse} 2s infinite;
    box-shadow: 0 4px 15px rgba(244, 67, 54, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.2);
    position: relative;
  }

  .card:hover {
    cursor: pointer;
    transform: translateY(-2px);
    animation-play-state: paused;
    box-shadow: 0 6px 20px rgba(244, 67, 54, 0.4);
  }

  .img {
    width: 50px;
    height: 50px;
    margin-left: 15px;
    border-radius: 12px;
    transition: 0.3s ease-in-out;
  }

  .error-icon {
    background: linear-gradient(135deg, #EF5350, #F44336);
    position: relative;
  }

  .error-icon::after {
    content: '⚠';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    font-size: 24px;
    font-weight: bold;
  }

  .textBox {
    width: calc(100% - 90px);
    margin-left: 15px;
    color: white;
    font-family: 'Poppins', sans-serif;
  }

  .textContent {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
  }

  .span {
    font-size: 11px;
    opacity: 0.8;
    background: rgba(255, 255, 255, 0.2);
    padding: 2px 6px;
    border-radius: 8px;
    font-weight: 500;
  }

  .h1 {
    font-size: 16px;
    font-weight: 600;
    margin: 0;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }

  .p {
    font-size: 13px;
    font-weight: 400;
    margin: 0;
    opacity: 0.9;
    line-height: 1.3;
  }
`;

export default ErrorNoticeCard;